import {
  type CreateLearningSessionRequest,
  createLearningSessionRequestSchema,
  type EventBatchRequest,
  type EventBatchResponse,
  eventBatchRequestSchema,
  eventBatchResponseSchema,
  type LearnerJoinRequest,
  type LearnerJoinResponse,
  type LearnerRecoverRequest,
  type LearnerRecoverResponse,
  type LearnerRecoveryRotateResponse,
  type LearningSessionResponse,
  learnerJoinRequestSchema,
  learnerJoinResponseSchema,
  learnerRecoverRequestSchema,
  learnerRecoverResponseSchema,
  learnerRecoveryRotateResponseSchema,
  learningSessionResponseSchema,
  type ProblemResponse,
  problemResponseSchema,
  type TaskAttemptRequest,
  type TaskAttemptResponse,
  taskAttemptRequestSchema,
  taskAttemptResponseSchema,
  uuidSchema,
} from "@history-sim/api-contracts";

const MAX_RESPONSE_BYTES = 256 * 1024;
const DEFAULT_TIMEOUT_MS = 10_000;

export class LearnerApiHttpError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly retryAfterMs: number | undefined;

  constructor(
    status: number,
    code: string,
    options: { readonly requestId?: string; readonly retryAfterMs?: number } = {},
  ) {
    super(`Learner API request failed with ${status}:${code}`);
    this.name = "LearnerApiHttpError";
    this.status = status;
    this.code = code;
    this.requestId = options.requestId;
    this.retryAfterMs = options.retryAfterMs;
  }
}

export class LearnerApiProtocolError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(`Learner API protocol validation failed: ${code}`);
    this.name = "LearnerApiProtocolError";
    this.code = code;
  }
}

export interface LearnerSyncApi {
  joinLearner(input: LearnerJoinRequest): Promise<LearnerJoinResponse>;
  recoverLearner(input: LearnerRecoverRequest): Promise<LearnerRecoverResponse>;
  rotateLearnerRecovery(csrfToken: string): Promise<LearnerRecoveryRotateResponse>;
  rotateCsrf(): Promise<string>;
  createLearningSession(
    input: CreateLearningSessionRequest,
    csrfToken: string,
  ): Promise<LearningSessionResponse>;
  appendEventBatch(
    serverSessionId: string,
    input: EventBatchRequest,
    csrfToken: string,
  ): Promise<EventBatchResponse>;
  submitTaskAttempt(
    serverSessionId: string,
    input: TaskAttemptRequest,
    csrfToken: string,
  ): Promise<TaskAttemptResponse>;
}

export interface LearnerApiClientOptions {
  readonly fetch?: typeof fetch;
  readonly timeoutMs?: number;
}

interface ResponseSchema<T> {
  safeParse(
    value: unknown,
  ): { readonly success: true; readonly data: T } | { readonly success: false };
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === "127.0.0.1" || hostname === "::1" || hostname === "localhost";
}

function timeout(value: number | undefined): number {
  const candidate = value ?? DEFAULT_TIMEOUT_MS;
  if (!Number.isSafeInteger(candidate) || candidate < 1 || candidate > 60_000) {
    throw new Error("Learner API timeout must be an integer between 1 and 60000 milliseconds");
  }
  return candidate;
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value || !/^[0-9]{1,6}$/u.test(value)) return undefined;
  const milliseconds = Number(value) * 1_000;
  return Number.isSafeInteger(milliseconds) ? Math.min(milliseconds, 60 * 60 * 1_000) : undefined;
}

async function readBoundedResponseText(response: Response): Promise<string> {
  const contentLengthValue = response.headers.get("content-length");
  if (contentLengthValue && /^[0-9]+$/u.test(contentLengthValue)) {
    const contentLength = Number(contentLengthValue);
    if (!Number.isSafeInteger(contentLength) || contentLength > MAX_RESPONSE_BYTES) {
      throw new LearnerApiProtocolError("RESPONSE_TOO_LARGE");
    }
  }

  if (!response.body) return "";
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let byteLength = 0;
  let text = "";
  try {
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      byteLength += result.value.byteLength;
      if (byteLength > MAX_RESPONSE_BYTES) {
        await reader.cancel();
        throw new LearnerApiProtocolError("RESPONSE_TOO_LARGE");
      }
      text += decoder.decode(result.value, { stream: true });
    }
    return text + decoder.decode();
  } finally {
    reader.releaseLock();
  }
}

export class LearnerApiClient implements LearnerSyncApi {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #timeoutMs: number;

  constructor(baseUrl: string, options: LearnerApiClientOptions = {}) {
    let parsed: URL;
    try {
      parsed = new URL(baseUrl);
    } catch {
      throw new Error("Learner API base URL is invalid");
    }
    if (
      (parsed.protocol !== "https:" &&
        !(parsed.protocol === "http:" && isLoopbackHostname(parsed.hostname))) ||
      parsed.username !== "" ||
      parsed.password !== "" ||
      parsed.pathname !== "/" ||
      parsed.search !== "" ||
      parsed.hash !== ""
    ) {
      throw new Error("Learner API base URL must be an exact HTTP(S) origin");
    }
    this.#baseUrl = parsed.origin;
    if (options.fetch) {
      const providedFetch = options.fetch;
      this.#fetch = (input, init) => providedFetch(input, init);
    } else {
      // Read the browser-global implementation at call time. Some embedded
      // WebViews expose an instrumented fetch that rejects a captured or bound
      // function even though a direct Window.fetch call is valid.
      this.#fetch = (input, init) => globalThis.fetch(input, init);
    }
    this.#timeoutMs = timeout(options.timeoutMs);
  }

  async joinLearner(input: LearnerJoinRequest): Promise<LearnerJoinResponse> {
    return this.#request(
      "/v1/auth/learner/join",
      learnerJoinRequestSchema.parse(input),
      learnerJoinResponseSchema,
    );
  }

  async recoverLearner(input: LearnerRecoverRequest): Promise<LearnerRecoverResponse> {
    return this.#request(
      "/v1/auth/learner/recover",
      learnerRecoverRequestSchema.parse(input),
      learnerRecoverResponseSchema,
    );
  }

  async rotateLearnerRecovery(csrfToken: string): Promise<LearnerRecoveryRotateResponse> {
    return this.#request(
      "/v1/auth/learner/recovery:rotate",
      {},
      learnerRecoveryRotateResponseSchema,
      csrfToken,
    );
  }

  async rotateCsrf(): Promise<string> {
    const schema = learnerJoinResponseSchema.pick({ csrfToken: true });
    const response = await this.#request("/v1/auth/csrf", {}, schema);
    return response.csrfToken;
  }

  async createLearningSession(
    input: CreateLearningSessionRequest,
    csrfToken: string,
  ): Promise<LearningSessionResponse> {
    return this.#request(
      "/v1/learning-sessions",
      createLearningSessionRequestSchema.parse(input),
      learningSessionResponseSchema,
      csrfToken,
    );
  }

  async appendEventBatch(
    serverSessionId: string,
    input: EventBatchRequest,
    csrfToken: string,
  ): Promise<EventBatchResponse> {
    const sessionId = uuidSchema.parse(serverSessionId);
    return this.#request(
      `/v1/learning-sessions/${encodeURIComponent(sessionId)}/events:batch`,
      eventBatchRequestSchema.parse(input),
      eventBatchResponseSchema,
      csrfToken,
    );
  }

  async submitTaskAttempt(
    serverSessionId: string,
    input: TaskAttemptRequest,
    csrfToken: string,
  ): Promise<TaskAttemptResponse> {
    const sessionId = uuidSchema.parse(serverSessionId);
    return this.#request(
      `/v1/learning-sessions/${encodeURIComponent(sessionId)}/task-attempts`,
      taskAttemptRequestSchema.parse(input),
      taskAttemptResponseSchema,
      csrfToken,
    );
  }

  async #request<T>(
    path: string,
    body: unknown,
    responseSchema: ResponseSchema<T>,
    csrfToken?: string,
  ): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.#timeoutMs);
    let response: Response;
    try {
      response = await this.#fetch(new URL(path, `${this.#baseUrl}/`), {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        redirect: "error",
        referrerPolicy: "strict-origin-when-cross-origin",
        headers: {
          Accept: "application/json, application/problem+json",
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (error) {
      if (controller.signal.aborted) throw new LearnerApiProtocolError("REQUEST_TIMEOUT");
      throw new LearnerApiProtocolError(
        error instanceof TypeError ? "NETWORK_UNAVAILABLE" : "REQUEST_FAILED",
      );
    } finally {
      clearTimeout(timer);
    }

    const contentType = response.headers.get("content-type")?.split(";", 1)[0]?.trim();
    if (contentType !== "application/json" && contentType !== "application/problem+json") {
      throw new LearnerApiProtocolError("RESPONSE_CONTENT_TYPE_INVALID");
    }
    const text = await readBoundedResponseText(response);

    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch {
      throw new LearnerApiProtocolError("RESPONSE_NOT_JSON");
    }

    if (!response.ok) {
      const problem = problemResponseSchema.safeParse(value);
      if (!problem.success || problem.data.status !== response.status) {
        const retryAfterMs = parseRetryAfter(response.headers.get("retry-after"));
        throw new LearnerApiHttpError(response.status, "INVALID_PROBLEM_RESPONSE", {
          ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
        });
      }
      throw this.#problemError(problem.data, response);
    }

    const parsed = responseSchema.safeParse(value);
    if (!parsed.success) throw new LearnerApiProtocolError("RESPONSE_SCHEMA_MISMATCH");
    return parsed.data;
  }

  #problemError(problem: ProblemResponse, response: Response): LearnerApiHttpError {
    const retryAfterMs = parseRetryAfter(response.headers.get("retry-after"));
    return new LearnerApiHttpError(problem.status, problem.code, {
      requestId: problem.requestId,
      ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
    });
  }
}
